import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Trash2,
  Zap,
  Edit2,
  Search,
  ChevronRight,
  ArrowUpDown,
  ListOrdered,
  List,
  Hash,
  ToggleLeft,
  AlignLeft,
  Info,
  Sliders,
  MapPin,
  HelpCircle,
  X,
  ListChecks,
} from 'lucide-react';
import { htmlToPlainText } from './utils';

const sectionMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
};

// Canonical type buckets. Order here = display order in the panel.
const TYPE_GROUPS = [
  { key: 'ordinal',     label: 'Ordinal',        short: 'ORD',  icon: ListOrdered },
  { key: 'nominal',     label: 'Nominal',        short: 'NOM',  icon: List },
  { key: 'numeric',     label: 'Numerical',      short: 'NRS',  icon: Hash },
  { key: 'vas',         label: 'Visual',         short: 'VAS',  icon: Sliders },
  { key: 'boolean',     label: 'Yes / No',       short: 'Y/N',  icon: ToggleLeft },
  { key: 'text',        label: 'Text',           short: 'TEXT', icon: AlignLeft },
  { key: 'instruction', label: 'Instruction',    short: 'INST', icon: Info },
  { key: 'body_map',    label: 'Body Map',       short: 'BODY', icon: MapPin },
  { key: 'other',       label: 'Other',          short: '—',    icon: HelpCircle },
  { key: 'irt',         label: 'IRT Calibrated', short: 'IRT',  icon: Zap },
];

const TYPE_GROUPS_BY_KEY = TYPE_GROUPS.reduce((acc, g) => {
  acc[g.key] = g;
  return acc;
}, {});

const normalizeType = (item) => {
  const t = (item.type || item.response_type || item.question_type || '').toLowerCase();
  if (t === 'likert') return 'ordinal';
  if (t === 'rating' || t === 'nrs') return 'numeric';
  if (t === 'yes_no') return 'boolean';
  if (TYPE_GROUPS_BY_KEY[t]) return t;
  return 'other';
};

const isIrtItem = (item) => {
  const irt = item.irt_parameters || {};
  const hasSlope = irt.slope !== undefined && irt.slope !== null;
  const hasDiscrimination = irt.discrimination !== undefined && irt.discrimination !== null;
  const hasThresholds =
    irt.thresholds !== undefined &&
    irt.thresholds !== null &&
    Array.isArray(irt.thresholds) &&
    irt.thresholds.length > 0;
  return hasSlope || hasDiscrimination || hasThresholds || item.category === 'C';
};

const SORT_OPTIONS = [
  { key: 'code',   label: 'Code A→Z' },
  { key: 'recent', label: 'Recently added' },
  { key: 'screen', label: 'On screen first' },
];

const sortItems = (list, sortBy, onScreenIds) => {
  const out = [...list];
  if (sortBy === 'code') {
    out.sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
  } else if (sortBy === 'recent') {
    out.sort((a, b) => (b.id || 0) - (a.id || 0));
  } else if (sortBy === 'screen') {
    out.sort((a, b) => {
      const aOn = onScreenIds.has(a.id) ? 0 : 1;
      const bOn = onScreenIds.has(b.id) ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true });
    });
  }
  return out;
};

const AssessmentItemsPanel = ({
  items,
  screens,
  currentScreenIndex,
  selectedElement,
  setSelectedElement,
  setShowItemBank,
  setDraggingFromSidebar,
  setDragPreviewPosition,
  addElementToScreen,
  removeAssessmentItem,
  removeAllItems,
  onEditItem,
}) => {
  const currentScreen = Array.isArray(screens) ? screens[currentScreenIndex] : undefined;

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('code');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onScreenIdSet = useMemo(
    () =>
      new Set(
        (currentScreen?.elements || [])
          .filter((el) => el.type === 'question' || el.type === 'instruction')
          .map((el) => el.questionId)
      ),
    [currentScreen]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((item) => {
      const code = (item.code || '').toLowerCase();
      const text = htmlToPlainText(item.text).toLowerCase();
      return code.includes(q) || text.includes(q);
    });
  }, [items, search]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const item of filtered) {
      const key = isIrtItem(item) ? 'irt' : normalizeType(item);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(item);
    }
    return TYPE_GROUPS
      .map((g) => ({ ...g, items: sortItems(buckets.get(g.key) || [], sortBy, onScreenIdSet) }))
      .filter((g) => g.items.length > 0);
  }, [filtered, sortBy, onScreenIdSet]);

  const totalCount = items?.length || 0;
  const visibleCount = filtered.length;

  const renderItemCard = (item, group) => {
    const existingElement = (currentScreen?.elements || []).find(
      (el) =>
        (el.type === 'question' || el.type === 'instruction') &&
        el.questionId === item.id
    );
    const isSelected = existingElement && existingElement.id === selectedElement;
    const isIrt = group.key === 'irt';

    return (
      <div key={item.id} className="relative group/item">
        <button
          type="button"
          draggable="true"
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/json', JSON.stringify({ type: 'question', item }));
            setDraggingFromSidebar({ type: 'question', item });
          }}
          onDragEnd={() => {
            setDraggingFromSidebar(null);
            setDragPreviewPosition(null);
          }}
          onClick={() => {
            if (existingElement) {
              if (setSelectedElement) setSelectedElement(existingElement.id);
            } else {
              addElementToScreen('question', item);
            }
          }}
          className={`flex w-full cursor-grab select-none flex-col items-start py-2.5 pl-10 pr-4 text-left transition-colors active:cursor-grabbing ${
            isSelected
              ? 'bg-primary-50/70 dark:bg-primary-400/10'
              : 'hover:bg-gray-50/70 dark:hover:bg-white/[0.03]'
          }`}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span className={`code-chip block min-w-0 truncate ${isSelected ? 'bg-primary-100/70 text-primary-800 dark:bg-primary-400/15 dark:text-primary-300' : ''}`}>
              {item.code || `Q${item.rank !== undefined ? item.rank + 1 : item.id}`}
            </span>
            <span className="flex flex-shrink-0 items-center gap-1.5 transition-opacity group-hover/item:opacity-0">
              <span className="badge badge-gray">
                {group.short}
              </span>
              {existingElement && (
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500"
                  title="On current screen"
                />
              )}
            </span>
          </div>
          <span className="pointer-events-none mt-1 line-clamp-2 whitespace-pre-line text-left text-[12.5px] leading-snug text-gray-600 dark:text-gray-400">
            {htmlToPlainText(item.text)}
          </span>
          {isIrt && item.irt_parameters && (
            <div className="mt-1.5 flex w-full flex-wrap gap-1">
              {(item.irt_parameters.slope || item.irt_parameters.discrimination) && (
                <span className="code-chip">
                  a={item.irt_parameters.slope || item.irt_parameters.discrimination}
                </span>
              )}
              {item.irt_parameters.thresholds && Array.isArray(item.irt_parameters.thresholds) && (
                <span className="code-chip">b=[{item.irt_parameters.thresholds.filter((t) => t !== -99).join(', ')}]</span>
              )}
            </div>
          )}
        </button>

        <div className="absolute right-3 top-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/item:opacity-100">
          {onEditItem && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditItem(item);
              }}
              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100/80 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
              title="Edit item"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              removeAssessmentItem(item);
            }}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-500/10 dark:hover:text-danger-400"
            title="Remove from assessment"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  };

  const renderGroup = (group) => {
    const expanded = expandedGroups.has(group.key);
    const onScreenCount = group.items.filter((i) => onScreenIdSet.has(i.id)).length;
    const Icon = group.icon;

    return (
      <div key={group.key}>
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-gray-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60 dark:hover:bg-white/[0.03]"
          aria-expanded={expanded}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Icon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                {group.label}
              </div>
              {onScreenCount > 0 && (
                <div className="mt-0.5 flex items-center gap-1.5 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                  {onScreenCount} on screen
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <span className="badge badge-gray tabular-nums">
              {group.items.length}
            </span>
            <motion.span
              animate={{ rotate: expanded ? 90 : 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="inline-flex"
            >
              <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </motion.span>
          </div>
        </button>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div {...sectionMotion} className="overflow-hidden">
              <div className="divide-y divide-gray-100 border-t border-gray-100 bg-gray-50/40 dark:divide-white/[0.06] dark:border-white/[0.06] dark:bg-white/[0.015]">
                {group.items.map((item) => renderItemCard(item, group))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-3.5">
        <div className="flex min-w-0 items-center gap-2">
          <ListChecks className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <h3 className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
            Assessment Items
          </h3>
          <span className="badge badge-gray tabular-nums">
            {totalCount}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {totalCount > 0 && removeAllItems && (
            <button
              type="button"
              onClick={removeAllItems}
              className="btn btn-danger-soft btn-sm btn-icon"
              title="Remove all items"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowItemBank(true)}
            className="btn btn-secondary btn-sm btn-icon"
            title="Import Items from Library"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {totalCount > 0 && (
        <div className="space-y-2 px-4 pb-3">
          {/* Search */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items..."
              className="input h-9 pl-8 pr-8"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-gray-400 transition-colors hover:bg-gray-100/80 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Sort + count */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {search
                ? `${visibleCount} of ${totalCount}`
                : `${totalCount} item${totalCount === 1 ? '' : 's'}`}
            </span>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <ArrowUpDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="input h-8 w-auto cursor-pointer pl-2.5 pr-8 text-xs"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {/* Groups */}
      <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
        {totalCount === 0 ? (
          <div className="px-4 py-7 text-center">
            <ListChecks aria-hidden="true" className="mx-auto mb-2 h-6 w-6 text-gray-400 dark:text-gray-500" />
            <p className="mb-3 text-[13px] text-gray-500 dark:text-gray-400">No items in this assessment</p>
            <button
              type="button"
              onClick={() => setShowItemBank(true)}
              className="btn btn-secondary btn-sm"
            >
              + Add from Item Library
            </button>
          </div>
        ) : grouped.length === 0 ? (
          <div className="px-4 py-6 text-center text-[13px] text-gray-500 dark:text-gray-400">
            No items match &ldquo;{search}&rdquo;
          </div>
        ) : (
          grouped.map(renderGroup)
        )}
      </div>
    </div>
  );
};

export default AssessmentItemsPanel;
